import React, { type Dispatch, type SetStateAction } from "react";

import { useSelector, useDispatch } from "react-redux";

import type { WhatnotIntegration } from "src/interfaces/plaidIntegration.interface";
import { type DataType } from "src/pages/GetStarted/ImportDialog";
import type MonthYear from "src/interfaces/monthYear.interface";
import { isWhatnotAllowedToDirectImportSelector } from "src/store/uploads/selector";
import {
  whatnotIntegrationDelete,
  whatnotIntegrationSyncChange,
} from "src/apiService/modules/whatnot";
import {
  whatnotSyncWithCheck,
  whatnotSyncSales,
} from "src/store/integrationSync/actions/whatnot";
import { IsRunningThreshold } from "src/store/integrationSync/actions/utils";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";

import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";
import useIsRunning from "../useIsRunning";
import type { DialogState } from "../types";

export function usePreventImportWhatnot(dataType: DataType) {
  return !useSelector((state: any) =>
    isWhatnotAllowedToDirectImportSelector(state, dataType)
  );
}

function WhatnotIntegrationTableRow({
  hideInventory,
  integration,
  setDialog,
}: {
  integration: WhatnotIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    integration.salesIsRunning,
    false,
    IsRunningThreshold
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || "Whatnot"}
      platform="Whatnot"
      isRunning={isRunning}
      sync={integration.sync}
      inventory={false}
      onPullDataNow={async () => {
        await dispatch(whatnotSyncWithCheck(integration.id, true));
      }}
      onImport={() => {
        setDialog({
          type: "import",
          props: {
            integrationId: integration.id,
            fetchTransactions: async (
              integrationId: string,
              start: MonthYear,
              end: MonthYear
            ) => {
              return dispatch(whatnotSyncSales(integrationId, { start, end }));
            },
            providerName: "Whatnot",
            usePreventImport: usePreventImportWhatnot,
            disableInventory: true,
          },
        });
      }}
      onSyncChanged={async (sync) => {
        await whatnotIntegrationSyncChange(integration.id, sync);
        if (sync) await dispatch(whatnotSyncWithCheck(integration.id, true));
      }}
      hideInventory={hideInventory}
      showInventoryCheckbox={false}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: whatnotIntegrationDelete,
          },
        })
      }
    />
  );
}

export default WhatnotIntegrationTableRow;
